import {forwardRef} from 'react';
import {cn} from '../lib/cn';
import {Checkbox, type CheckboxOption} from '../checkbox';
import {type CheckboxDropdownGroupProps} from './types';
import {DropdownMenuItem} from './DropdownMenu';

const CustomCheckboxDropdownGroup = forwardRef<
  HTMLDivElement,
  CheckboxDropdownGroupProps
>(({options, value = [], onChange, keepOpenOnSelect, className = '', multiple}, ref) => {
  const isMultipleMode = multiple ?? Array.isArray(value);
  const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];

  const handleCheckboxChange = (optionValue: CheckboxOption, checked: boolean) => {
    if (isMultipleMode) {
      const newValue = checked
        ? [...normalizedValue, optionValue]
        : normalizedValue.filter(v => v.value !== optionValue.value);
      (onChange as (selectedValues: CheckboxOption[]) => void)(newValue);
    } else {
      if (checked) {
        (onChange as (selectedValues: CheckboxOption) => void)(optionValue);
      } else {
        (onChange as (selectedValues: CheckboxOption | null) => void)(null);
      }
    }
  };

  const isChecked = (optionValue: string) =>
    normalizedValue.some(v => v.value === optionValue);

  const renderCheckboxItem = (option: CheckboxOption, idx: number) => {
    const checked = option.checked ?? isChecked(option.value);

    return (
      <DropdownMenuItem
        key={option.label + idx}
        onClick={() => {
          const newChecked = !checked;

          if (!isMultipleMode && newChecked) {
            handleCheckboxChange(option, newChecked);
          } else {
            handleCheckboxChange(option, newChecked);
          }
        }}
        onSelect={e => {
          if (keepOpenOnSelect) {
            e.preventDefault();
          }
        }}
      >
        <Checkbox
          key={option.value}
          id={`checkbox-${option.value}`}
          value={checked}
          disabled={option.disabled}
          className={cn(option.disabled && 'cursor-not-allowed opacity-50 w-auto')}
        />
        {option.label}
      </DropdownMenuItem>
    );
  };

  return (
    <div ref={ref} className={className}>
      {options.map(renderCheckboxItem)}
    </div>
  );
});

CustomCheckboxDropdownGroup.displayName = 'CustomCheckboxGroup';

export {CustomCheckboxDropdownGroup};
